/**
 * Contenu du Centre d'aide.
 * Chaque topic = { id, title, category, audience[], icon, sections[] }
 * audience = quels rôles voient ce topic (superadmin, admin, tenant_admin, all + business roles)
 */

export const HELP_CATEGORIES = [
  { id: "getting_started", label: "Premiers pas", icon: "Rocket" },
  { id: "ai", label: "Intelligence Artificielle", icon: "Bot" },
  { id: "team", label: "Équipe & rôles", icon: "Users" },
  { id: "modules", label: "Modules & abonnement", icon: "Layers" },
  { id: "marketing", label: "Marketing", icon: "Megaphone" },
  { id: "facturation", label: "Facturation", icon: "FileText" },
  { id: "formation", label: "Formation & LMS", icon: "GraduationCap" },
  { id: "master", label: "Administration master", icon: "Crown" },
  { id: "security", label: "Sécurité & maintenance", icon: "Shield" },
];

export const HELP_TOPICS = {
  examens_certifiants: {
    title: "Examens certifiants — de la session au diplôme",
    category: "formation",
    audience: ["superadmin", "admin", "tenant_admin"],
    icon: "GraduationCap",
    sections: [
      { title: "À quoi sert le module ?", body: "Le module Examens certifiants (barre latérale > Formation & LMS > Examens certifiants) pilote vos sessions d'examen de bout en bout : certifications, candidats, jury, sujets, résultats, délibération et diplômes. Il est générique : la référence au répertoire (RS/RNCP) est optionnelle et chaque certification définit librement son QCM (barème, seuil) et ses épreuves pratiques." },
      { title: "Créer une certification puis une session", body: "1. Onglet Certifications : créez la certification (titre, organisme, compétences évaluées, config QCM et pratique).\n2. Onglet Sessions : créez une session (date, lieu, responsable, jury). Une checklist de préparation suit l'avancement (sujets, convocations, jury).\n3. Ajoutez les candidats (nom, prénom, email) — l'email sert aux convocations et à l'envoi du diplôme." },
      { title: "Sujets & QCM générés par IA", body: "Dans l'onglet Sujets, l'IA génère les sujets pratiques (3 variantes par défaut) et un QCM complet à partir des compétences de la certification. Le corrigé du QCM est réservé au jury. Vous pouvez régénérer ou éditer chaque sujet avant validation." },
      { title: "Jour J : émargement, résultats, délibération", body: "Cochez l'émargement de chaque candidat, saisissez le score QCM et la grille d'évaluation pratique. L'onglet Délibération calcule automatiquement la décision (certifié / ajourné / absent) selon les seuils de la certification ; le jury peut ajuster avant clôture." },
      { title: "Documents : dossier ZIP complet + diplômes PDF", body: "L'onglet Documents génère chaque document (convocations, PV de délibération, grilles jury, attestations...) au format DOCX, et le bouton « Exporter le dossier complet (ZIP) » télécharge tout le dossier d'examen en un seul fichier — y compris les diplômes PDF des candidats certifiés." },
      { title: "Emails automatiques", body: "• Convocations : envoyées par email aux candidats en un clic (via le SMTP du Hub API).\n• Rappel J-3 : un automatisme quotidien relance les candidats non émargés 3 jours avant l'examen.\n• Diplômes : à la clôture de la session, chaque candidat certifié reçoit automatiquement son diplôme PDF par email, avec son lien de consultation." },
      { title: "Portail candidat & statistiques", body: "Chaque candidat dispose d'un lien public sécurisé (token) pour consulter son résultat : décision, scores et téléchargement du diplôme. L'onglet Statistiques suit l'évolution du taux de réussite par certification et par session." },
    ],
  },
  previsionnel_banque: {
    title: "Prévisionnel financier 3 ans (dossier banque)",
    category: "facturation",
    audience: ["superadmin", "admin", "tenant_admin"],
    icon: "FileText",
    sections: [
      { title: "Objectif", body: "Le Prévisionnel banque (Comptabilité & Finances > Previsionnel banque) construit un prévisionnel financier sur 36 mois par société : chiffre d'affaires, charges, masse salariale, EBE, impôt sur les sociétés, annuités d'emprunt et courbe de trésorerie. Idéal pour un dossier de financement bancaire." },
      { title: "Hypothèses pré-remplies avec vos données réelles", body: "À la création d'un scénario, la plateforme pré-remplit les hypothèses à partir de vos données : CA mensuel moyen (encaissements), dépenses moyennes, masse salariale et pipeline commercial pondéré. Vous ajustez ensuite : croissance annuelle, charges variables (%), embauches, investissements, emprunt (montant, taux, durée) et apport." },
      { title: "Calculs automatiques", body: "Le moteur calcule mois par mois : CA, charges, EBE, dotations, résultat avant impôt, IS (15 % jusqu'à 42 500 € puis 25 %), mensualités d'emprunt et trésorerie cumulée. Les totaux annuels (An 1 / An 2 / An 3) sont présentés en tableau et en graphiques." },
      { title: "Scénarios & comparateur", body: "Créez plusieurs scénarios par société (ex : prudent vs ambitieux) et ouvrez le Comparateur pour les mettre côte à côte : CA, EBE, résultat net, trésorerie finale et point bas de trésorerie. Parfait pour arbitrer avant un rendez-vous bancaire." },
      { title: "Export PDF", body: "Chaque scénario s'exporte en PDF professionnel aux couleurs de la société (identité visuelle du builder) : hypothèses, compte de résultat prévisionnel 3 ans, plan de financement et courbe de trésorerie." },
    ],
  },
  identite_visuelle_societe: {
    title: "Identité visuelle par société (logo & couleurs)",
    category: "modules",
    audience: ["superadmin", "admin", "tenant_admin"],
    icon: "Building2",
    sections: [
      { title: "Le builder d'identité visuelle", body: "Chaque société de votre organisation peut avoir son identité propre : logo et palette de couleurs. Rendez-vous dans Paramètres > Sociétés > modifier une société : téléversez le logo et choisissez la couleur d'identification." },
      { title: "Où est-elle utilisée ?", body: "L'identité de la société s'applique automatiquement : bandeau de contexte société (sidebar), documents PDF générés pour cette société (ex : prévisionnel banque aux couleurs de la société) et écrans qui affichent le contexte multi-sociétés." },
      { title: "Différence avec la marque blanche", body: "La marque blanche (Administration > Marque blanche) personnalise la plateforme entière pour votre organisation (logo de connexion, thème). L'identité visuelle par société, elle, différencie chaque société AU SEIN de votre organisation." },
    ],
  },
  hub_api_societe: {
    title: "Hub API — clés par organisation et par société",
    category: "modules",
    audience: ["superadmin", "admin", "tenant_admin"],
    icon: "RefreshCw",
    sections: [
      { title: "Le Hub API en bref", body: "Le Hub API (barre latérale, en bas) centralise le paramétrage ET la surveillance de toutes vos intégrations : SMTP, Aircall, Yousign, Meta, GoCardless, agent enquêteur IA... Chaque carte affiche son statut en temps réel (Opérationnel / À configurer / Erreur) et un formulaire de configuration. Les clés sont chiffrées en base et jamais réaffichées en clair." },
      { title: "Portée : organisation ou société", body: "En haut de la page, le sélecteur « Portée de la configuration » permet de basculer entre la configuration de l'organisation (par défaut) et celle d'une société précise. En mode société, chaque intégration affiche un badge : « Héritée du tenant » (elle utilise les clés de l'organisation) ou « Propre à la société » (elle a ses propres clés)." },
      { title: "Surcharger les clés d'une société", body: "Sélectionnez la société, cliquez Configurer sur une carte (SMTP, Aircall, Yousign, Meta ou GoCardless) et saisissez les clés propres à cette société. Exemple concret : chaque société envoie ses factures et relances avec SON propre domaine d'email. Laissez un champ vide pour conserver l'héritage. Le bouton « Hériter » supprime la surcharge et revient aux clés de l'organisation." },
      { title: "Ce qui reste au niveau organisation", body: "Les modèles IA (clé universelle Emergent, agent enquêteur) et les intégrations plateforme (Cegid, Stripe, Storecove, Google) ne sont pas surchargeables par société — ils restent gérés au niveau organisation ou par l'équipe Blitzz." },
    ],
  },
  getting_started: {
    title: "Premiers pas sur Blitzz",
    category: "getting_started",
    audience: ["all"],
    icon: "Rocket",
    sections: [
      {
        title: "Bienvenue",
        body: "Blitzz est une plateforme SaaS multi-tenant pour piloter votre activité : CRM, facturation, formation, comptabilité, marketing Google. Chaque utilisateur voit uniquement les modules activés pour sa société et ses rôles.",
      },
      {
        title: "Connexion",
        body: "Connectez-vous avec votre email et mot de passe (login/Google OAuth). Si vous avez perdu votre mot de passe, utilisez 'Mot de passe oublié' sur l'écran de connexion.",
      },
      {
        title: "Tableau de bord",
        body: "Le dashboard affiche les KPIs principaux selon votre rôle : volume d'affaires (commercial), inscriptions formation (formateur), trésorerie (comptable), etc.",
      },
      {
        title: "Sidebar et navigation",
        body: "La sidebar gauche regroupe vos modules par catégorie (Force de vente, Formation, Marketing...). Si un module n'apparaît pas, c'est qu'il n'est pas activé pour votre tenant ou société.",
      },
    ],
  },
  ai_scoring: {
    title: "Profil IA & scoring de leads",
    category: "ai",
    audience: ["all"],
    icon: "Bot",
    sections: [
      {
        title: "Comment fonctionne le scoring IA ?",
        body: "Pour chaque prospect ou opportunité, l'IA Gemini 2.5 Pro analyse l'ensemble du dossier (notes, activités, résumés d'appels Aircall, devis, factures) et produit un score de 0 à 100, un tier (CHAUD / TIÈDE / FROID), un résumé de 3-4 phrases, des points forts, des risques, des actions recommandées et des signaux d'achat détectés.",
      },
      {
        title: "Déclenchement de l'évaluation",
        body: "L'évaluation se déclenche **automatiquement** à la fin de chaque appel Aircall avec résumé IA, et **manuellement** via le bouton 'Évaluer IA' dans le hero de la fiche détaillée. Vous pouvez la relancer autant de fois que vous voulez — chaque évaluation prend 10-20 secondes.",
      },
      {
        title: "Lecture du profil IA",
        body: "Sur la fiche complète (`/admin/prospect/:id` ou `/admin/opportunite/:id`), l'onglet 'Profil IA' affiche : score + tier coloré (rose chaud / ambre tiède / bleu froid), confiance (0-100%), résumé, 3 colonnes (forces / risques / actions), badges des signaux d'achat détectés, statut recommandé.",
      },
      {
        title: "Auto-création d'activité Premier RDV",
        body: "Dès qu'un lead passe en CHAUD pour la 1ʳᵉ fois, une activité '🔥 Premier rendez-vous proposé' est créée automatiquement dans son historique avec l'action recommandée par l'IA. Visible immédiatement dans la timeline.",
      },
      {
        title: "Fallback heuristique",
        body: "Si la clé Gemini est indisponible, un fallback heuristique déterministe prend le relais (basé sur appels/devis/CA/recency). Le widget affiche '⚙️ Évaluation heuristique' au lieu de '🤖 Gemini'.",
      },
    ],
  },
  questionnaire_analysis: {
    title: "Analyse IA des questionnaires conditionnels",
    category: "ai",
    audience: ["all"],
    icon: "ClipboardList",
    sections: [
      {
        title: "Qu'est-ce que c'est ?",
        body: "Quand un prospect remplit un questionnaire de qualification (ou que vous importez une liste de leads avec leurs réponses), l'IA Gemini 2.5 Pro analyse **chaque réponse par rapport à la question posée** et produit une qualification structurée : score 0-100, tier CHAUD/TIÈDE/FROID, points forts, lacunes, signaux négatifs, action recommandée.",
      },
      {
        title: "Quand l'analyse se déclenche-t-elle ?",
        body: "**Automatiquement** : (1) à l'import d'un lead avec `questionnaire_responses` ; (2) chaque fois qu'on enregistre les réponses du questionnaire d'un prospect via `PUT /prospects/{id}/questionnaire-responses`. **Manuellement** : bouton « Réanalyser » dans la fiche détail.",
      },
      {
        title: "Affichage dans la fiche prospect",
        body: "Sur la **fiche complète** d'un prospect, deux endroits :\n- **Onglet Aperçu** : un résumé compact cliquable (verdict + score + action recommandée). Clic → ouvre une fenêtre popover avec l'analyse IA et la liste complète des Q/A par section.\n- **Onglet Questionnaire** dédié : l'analyse complète + toutes les questions/réponses regroupées par section (Identité / Timing / Budget / Besoins…).",
      },
      {
        title: "Champs conditionnels",
        body: "Les champs masqués par les conditions du questionnaire ne sont **PAS** considérés comme manquants par l'IA — leur absence est traitée comme normale. Seuls les champs réellement visibles à l'utilisateur sont pris en compte dans le scoring.",
      },
      {
        title: "Format des réponses pour l'import batch",
        body: "Pour importer une liste de leads avec leurs réponses : `POST /api/prospects/import` avec `{leads: [{first_name, email, questionnaire_id, questionnaire_responses: {field_id: value, ...}}]}`. L'analyse IA est lancée en background pour chaque lead — la réponse de l'API contient `ai_analyses_scheduled: N`.",
      },
    ],
  },
  audiences_ia: {
    title: "Audiences publicitaires IA (Meta & Google Ads)",
    category: "ai",
    audience: ["all"],
    icon: "Megaphone",
    sections: [
      {
        title: "Qu'est-ce que ça fait ?",
        body: "Le module convertit en 1 clic vos **prospects HOT/WARM scorés** par l'IA en un **brief publicitaire complet** prêt pour Meta Ads et Google Ads : persona cible, mots-clés Google avec match_type, intérêts Meta, headlines, descriptions, budget recommandé, CPA cible, ROAS attendu et stratégie de campagne — le tout généré par Gemini 2.5 Pro à partir des signaux d'achat réels détectés sur vos leads.",
      },
      {
        title: "Comment ça marche ?",
        body: "1. Allez sur **Marketing Google → onglet Audiences IA**.\n2. Sélectionnez vos critères (tiers chaud/tiède/froid + score minimum).\n3. Vérifiez le **preview live** (audience, score moyen, top module, top statut).\n4. Cliquez sur **« Générer un brief IA »** — Gemini analyse en 10-15 secondes.\n5. Consultez le brief : persona, motivations, objections, mots-clés, intérêts, ad copy, budget.\n6. Cliquez **« Exporter pour Meta / Google Ads »** → fichier CSV prêt à uploader.",
      },
      {
        title: "Boucle d'apprentissage IA — ROI réel",
        body: "Après avoir lancé une campagne, importez vos leads avec le paramètre `audience_id` pour les rattacher automatiquement à l'audience source. Le **panel ROI** affiche alors : leads générés vs prédiction IA, conversions, CA réel, ROAS effectif vs prévu, et un **feedback IA** ('performance', 'sous-performance', 'vigilance') qui indique si l'audience tient ses promesses ou si elle nécessite un re-targeting.",
      },
      {
        title: "Format du CSV exporté",
        body: "`email, phone, fn, ln, company, ai_score, ai_tier`\n\nCe format est compatible directement avec :\n- **Meta Custom Audience** (email + phone hashés automatiquement par Meta).\n- **Google Customer Match** (email + phone).",
      },
      {
        title: "Audience trop petite ?",
        body: "L'API exige un **minimum de 3 prospects scorés** pour générer un brief pertinent. Si vous n'avez pas assez de leads scorés, lancez d'abord des évaluations IA depuis vos fiches prospects (ou activez le **mode démo** pour générer un jeu de données cohérent en 1 clic).",
      },
    ],
  },
  hot_leads_page: {
    title: "Page « Leads chauds » plein écran",
    category: "ai",
    audience: ["all"],
    icon: "Flame",
    sections: [
      {
        title: "Vue centralisée",
        body: "Accessible via la sidebar **Leads chauds**, cette page liste tous vos prospects et opportunités scorés par l'IA, triés par score décroissant. Idéal pour démarrer la journée commercial : vous voyez en un coup d'œil qui rappeler en priorité.",
      },
      {
        title: "Filtres avancés",
        body: "**Recherche** par nom/email/société · **Type** (prospect/opportunité/tous) · **Score min.** · **Toggles tiers** (chaud/tiède/froid). Les filtres se combinent et la liste se met à jour instantanément.",
      },
      {
        title: "Click-to-call & ouverture rapide",
        body: "Sur chaque ligne, **bouton téléphone vert** pour appeler immédiatement via Aircall, **bouton flèche jaune** pour ouvrir la fiche complète. Le verdict IA et l'action recommandée sont affichés directement dans le tableau.",
      },
    ],
  },
  email_draft_ia: {
    title: "Email IA personnalisé pour leads HOT",
    category: "ai",
    audience: ["all"],
    icon: "Mail",
    sections: [
      {
        title: "Génération automatique",
        body: "Dès qu'un prospect passe en tier CHAUD, l'IA Gemini génère **automatiquement** un draft d'email de prise de RDV personnalisé qui fait écho aux signaux d'achat détectés et propose 1-2 créneaux concrets dans les 48h. Le draft apparaît dans l'onglet **Profil IA** de la fiche prospect.",
      },
      {
        title: "Édition et envoi",
        body: "Vous pouvez **éditer** le sujet et le corps avant envoi. 3 actions disponibles :\n- **Copier** : copie subject + body dans le presse-papier.\n- **Ouvrir dans ma messagerie** : lance votre client mail (Gmail, Outlook…) avec le destinataire et le contenu pré-remplis.\n- **Marquer envoyé** : crée une activity 'email' dans la timeline du prospect.",
      },
      {
        title: "Régénérer",
        body: "Si le draft ne vous plaît pas, cliquez sur **Régénérer** pour relancer Gemini avec un nouvel angle. Vous pouvez le régénérer autant de fois que voulu.",
      },
    ],
  },
  pdf_export: {
    title: "Export PDF de la fiche prospect",
    category: "ai",
    audience: ["all"],
    icon: "FileText",
    sections: [
      {
        title: "Document professionnel branding Blitzz",
        body: "Sur la fiche complète d'un prospect (`/admin/prospect/:id`), cliquez sur le bouton **« Exporter PDF »** dans le hero pour télécharger un PDF Axonaut-style avec :\n- Hero (nom, contact, badge IA score)\n- 4 KPI cards (appels, durée, devis/factures, CA encaissé)\n- Profil IA complet (forces / risques / actions)\n- Notes\n- Derniers appels Aircall avec résumés IA\n- Activité récente",
      },
      {
        title: "Cas d'usage",
        body: "Idéal pour : envoyer un brief à un commercial mobile, archiver un dossier signé, transmettre un lead à un partenaire, ou préparer un point hebdo équipe. Le PDF est généré côté serveur (reportlab) et téléchargé en quelques secondes.",
      },
    ],
  },
  hot_leads_widget: {
    title: "Widget « À rappeler aujourd'hui »",
    category: "ai",
    audience: ["all"],
    icon: "Flame",
    sections: [
      {
        title: "À quoi sert ce widget ?",
        body: "Affiche en haut du Dashboard les **5 leads les plus chauds du moment** (prospects + opportunités confondus) classés par score IA décroissant. Permet à un commercial de savoir en 2 secondes qui rappeler en priorité.",
      },
      {
        title: "Click-to-call Aircall direct",
        body: "Chaque carte a un bouton téléphone vert qui déclenche immédiatement un appel sortant via Aircall (si l'intégration est configurée pour votre tenant). L'appel arrive sur votre softphone Aircall en quelques secondes.",
      },
      {
        title: "Action recommandée IA",
        body: "Sous chaque lead, l'IA affiche **la 1ʳᵉ action prioritaire** suggérée (ex : 'Établir et envoyer le devis demandé sous 24h'). Suivez la suggestion ou cliquez sur la carte pour ouvrir la fiche complète.",
      },
      {
        title: "Mes leads n'apparaissent pas ?",
        body: "Le widget filtre uniquement les leads **avec un score ≥ 50 et un statut actif** (pas perdus, pas convertis, pas gagnés/perdus pour les opportunités). Si rien ne s'affiche, lancez une évaluation IA depuis la fiche détaillée.",
      },
    ],
  },
  client_fiche_detail: {
    title: "Fiches Prospect / Opportunité (style Axonaut)",
    category: "ai",
    audience: ["all"],
    icon: "FileText",
    sections: [
      {
        title: "Hero header complet",
        body: "Avatar lettre coloré, nom + entreprise, badges (statut + score IA), headline IA en italique, contact pills cliquables (email mailto / téléphone tel), tags. Boutons d'action : Appeler (Aircall), changement de statut/stage, Convertir en CRM (depuis prospect), Évaluer IA.",
      },
      {
        title: "Bandeau KPIs",
        body: "5 à 6 cartes condensées : Appels (entrants/sortants), Durée totale, Devis (signés/total), CA encaissé, Activités (prospect) ou Montant + Pondéré (opportunité).",
      },
      {
        title: "Onglets",
        body: "**Aperçu** (résumé IA compact + note rapide + activité récente) · **Profil IA** (analyse complète) · **Timeline** (feed unifié notes/appels/évaluations) · **Appels** (résumés Aircall avec écoute enregistrement) · **Devis & Factures** (liste cliquable).",
      },
      {
        title: "Accès rapide",
        body: "Depuis la liste des prospects ou le Kanban CRM, le badge AI est affiché à côté du statut. Cliquez sur la ligne pour ouvrir le Dialog rapide, ou cliquez sur 'Fiche complète' (icône ↗) pour la page dédiée.",
      },
    ],
  },
  onboarding_tour: {
    title: "Tutoriel interactif et bouton « Activer le tutoriel »",
    category: "getting_started",
    audience: ["all"],
    icon: "Play",
    sections: [
      {
        title: "Quand le tour s'affiche-t-il ?",
        body: "Le tour interactif se déclenche **automatiquement à votre 1ʳᵉ visite** du Dashboard (3 étapes pour les admins, 4 pour les tenants). Il met en surbrillance chaque élément clé avec une popover explicative.",
      },
      {
        title: "Naviguer dans le tour",
        body: "Boutons **Précédent / Suivant / Passer le tutoriel / X**. La progression est mémorisée — si vous le passez, il ne se relancera pas automatiquement.",
      },
      {
        title: "Réactiver le tutoriel",
        body: "Vous pouvez relancer le tour à tout moment depuis le Centre d'aide en cliquant sur le bouton **« Activer le tutoriel »** en haut à droite. Vous serez redirigé(e) vers le Dashboard et le tour démarrera.",
      },
    ],
  },
  cache_updates: {
    title: "Mises à jour & cache navigateur",
    category: "security",
    audience: ["all"],
    icon: "RefreshCw",
    sections: [
      {
        title: "Détection automatique des nouvelles versions",
        body: "Quand une mise à jour de la plateforme est déployée, un bandeau doré 'Nouvelle version disponible' apparaît en bas de page. Cliquez sur 'Mettre à jour' pour vider le cache et recharger automatiquement. Vous gardez toujours la dernière version sans intervention.",
      },
      {
        title: "Désactiver la détection automatique",
        body: "Allez dans Paramètres > Sécurité > Cache & Service Worker. Le toggle 'Détection automatique des nouvelles versions' permet d'éteindre les notifications. Recommandé : laisser activé.",
      },
      {
        title: "Effacer le cache manuellement",
        body: "Si vous voyez une ancienne version de l'interface ou des bugs visuels, allez dans Paramètres > Sécurité et cliquez 'Effacer le cache et recharger'. Cette action vide le Service Worker, le localStorage et l'IndexedDB du navigateur.",
      },
    ],
  },
  account_security: {
    title: "Compte & sécurité",
    category: "security",
    audience: ["all"],
    icon: "Shield",
    sections: [
      {
        title: "Changer son mot de passe",
        body: "Cliquez sur votre avatar (haut droite) > Mon profil > Sécurité. Saisissez l'ancien mot de passe puis le nouveau (8 caractères minimum, majuscule + chiffre recommandés).",
      },
      {
        title: "Gérer ses sessions",
        body: "L'onglet Sessions liste les appareils connectés. Vous pouvez déconnecter à distance n'importe quel appareil suspect.",
      },
      {
        title: "Authentification à deux facteurs (2FA)",
        body: "Le 2FA peut être activé dans Paramètres > Sécurité par les administrateurs (toggle dédié). Les utilisateurs scannent un QR code avec Google Authenticator ou similaire.",
      },
    ],
  },
  team_management: {
    title: "Gérer son équipe",
    category: "team",
    audience: ["admin", "superadmin", "tenant_admin"],
    icon: "Users",
    sections: [
      {
        title: "Inviter un membre",
        body: "Allez dans Équipe > Inviter. Saisissez email + nom puis choisissez 2 paramètres : Type d'accès (Superadmin/Admin/User/Développeur — privilèges techniques) ET Rôles métier (multi-select : Formateur, Stagiaire, Comptable, Coach, Commercial, Sous-traitant — espaces fonctionnels accessibles).",
      },
      {
        title: "Type d'accès vs Rôle métier",
        body: "Distinction importante : le type d'accès détermine ce que la personne peut administrer (admin = tout son tenant, user = membre standard). Le rôle métier détermine quels espaces fonctionnels elle voit (un Formateur voit Espace formateur, un Stagiaire voit Mes parcours). Une même personne peut avoir plusieurs rôles métier (ex: admin + formateur + comptable).",
      },
      {
        title: "Filtrer la liste",
        body: "Utilisez les 2 dropdowns en haut de la liste équipe pour filtrer par Type d'accès et/ou Rôle métier. Le bouton 'Réinit.' efface tous les filtres.",
      },
      {
        title: "Supprimer un membre",
        body: "Cliquez sur l'icône poubelle en bout de ligne. Attention : supprimer un membre supprime aussi ses données associées (factures émises, sessions formées). Considérez plutôt désactiver son compte.",
      },
    ],
  },
  tenant_settings: {
    title: "Paramètres du tenant",
    category: "modules",
    audience: ["admin", "tenant_admin"],
    icon: "Building2",
    sections: [
      {
        title: "Mes modules activés",
        body: "Le widget 'Mes modules activés' (Paramètres tenant) liste tous vos modules regroupés par catégorie avec couleurs distinctes. Si un module manque, contactez votre conseiller Blitzz pour l'ajouter à votre offre.",
      },
      {
        title: "Mon abonnement",
        body: "Le panel 'Mon abonnement' affiche votre offre actuelle + addons. Toggle Mensuel/Annuel pour comparer les prix. Si vous n'avez pas encore d'abonnement actif, les offres disponibles s'affichent et vous pouvez vous abonner directement via Stripe.",
      },
      {
        title: "Marque blanche (white-label)",
        body: "Si votre offre inclut la marque blanche, vous pouvez personnaliser logo, couleurs, nom de la plateforme dans Paramètres > Branding. Vos utilisateurs verront votre marque au lieu de Blitzz.",
      },
      {
        title: "Sociétés multi",
        body: "Si votre offre inclut le multi-sociétés, gérez vos sociétés dans Paramètres > Sociétés. Chaque société peut avoir ses propres modules activés (ex: Société A = Formation only, Société B = Formation + Comptabilité).",
      },
    ],
  },
  modules_overview: {
    title: "Vue d'ensemble des modules",
    category: "modules",
    audience: ["admin", "tenant_admin", "superadmin"],
    icon: "Layers",
    sections: [
      {
        title: "Force de vente",
        body: "CRM (clients/prospects), Facturation (devis/factures/avoirs), Pilotage commercial. Permet de suivre le pipeline et générer des documents.",
      },
      {
        title: "Formation",
        body: "LMS complet : programmes, modules, sessions, évaluations, inscriptions, certifications RS/RNCP. Inclut export EDOF et stats BPF.",
      },
      {
        title: "Marketing",
        body: "Réseaux sociaux (calendrier + IA), Mailing (campagnes), Google (SEO + GMB + Ads), Briefs hebdo. Pilotés par agents IA spécialisés.",
      },
      {
        title: "Comptabilité",
        body: "Plan comptable, écritures, FEC export, rapprochement bancaire, trésorerie. Auto-postage des factures et paiements.",
      },
      {
        title: "Mon Espace",
        body: "Espace personnel : mes tâches, mon équipement, mes dépenses, mes documents, ma messagerie, mon agenda.",
      },
    ],
  },
  marketing_google: {
    title: "Marketing Google (SEO + GMB + Ads)",
    category: "marketing",
    audience: ["admin", "tenant_admin", "commercial"],
    icon: "Globe",
    sections: [
      {
        title: "Module SEO",
        body: "Ajoutez vos sites web (clients ou propres). Lancez des audits SEO automatiques : score /100, points forts, points à corriger (priorités haute/moyenne/basse), données techniques (titre, meta, H1, schema, images alt, etc.). L'IA Gemini propose des actions concrètes priorisées.",
      },
      {
        title: "Module Google Business",
        body: "Connectez vos fiches GMB (Phase 2 : OAuth Google). Listez tous vos avis, filtrez par note ou statut (répondu/non répondu). Cliquez 'Réponse IA' pour générer 2 versions (courte + longue) personnalisées au ton de l'avis.",
      },
      {
        title: "Module Google Ads",
        body: "Vue de vos campagnes avec métriques 30j (impressions, CTR, conversions, CPA). Bouton 'Créer campagne IA' qui drafte une campagne complète : objectif, audience, mots-clés, headlines + descriptions, budget journalier suggéré.",
      },
      {
        title: "Agent IA Gemini sticky",
        body: "La sidebar droite contient l'Agent IA conversationnel (Gemini 2.5 Pro). Vous pouvez lui demander n'importe quoi sur le SEO, GMB, Ads. Il garde l'historique des conversations (sessions persistées) et propose des suggestions de démarrage.",
      },
      {
        title: "Phase 1 vs Phase 2",
        body: "Phase 1 (actuelle) : SEO 100% fonctionnel (audit réel sur vraies pages), GMB et Ads en mode démonstration avec données mockées réalistes. Phase 2 (à venir avec credentials Google) : connexion OAuth réelle aux fiches GMB et comptes Ads.",
      },
    ],
  },
  workflows_automation: {
    title: "Workflows & Automations IA",
    category: "marketing",
    audience: ["admin", "tenant_admin"],
    icon: "Zap",
    sections: [
      {
        title: "Vue d'ensemble (Marketing > Workflows)",
        body: "Les Workflows sont le moteur d'automatisation façon GoHighLevel : un Trigger déclenche une cascade d'Actions (email, SMS, tâche, tag, IA Gemini, OneDrive folder, branche IF). Accessible via la sidebar Marketing > Workflows & Automations.",
      },
      {
        title: "Triggers disponibles",
        body: "Lead créé · Tag ajouté · Score IA seuil · Lead converti · Appel terminé/transcrit · Formulaire soumis · Stripe payment success/failed · Webhook entrant · Message Meta reçu (WhatsApp/FB/IG). Chaque trigger transmet un contexte enrichi (lead, call, thread, message).",
      },
      {
        title: "Actions disponibles (13 types)",
        body: "Email, SMS, Tâche, Ajouter/Retirer tag, MAJ prospect, Note, Notification interne, Webhook sortant, IA Gemini (génération contenu), Créer dossier OneDrive, Condition IF (branche), Délai (attente).",
      },
      {
        title: "Builder visuel (Iter 222)",
        body: "Toggle 'Visuel/Liste' dans l'éditeur. Vue Visuel = drag & drop avec react-flow, palette flottante d'actions, mini-map. Vue Liste = édition fine du JSON config par action. Variables interpolables : {{lead.name}}, {{thread.channel}}, {{message.text}}, etc.",
      },
      {
        title: "Templates prêts à l'emploi",
        body: "10+ templates fournis : Onboarding client (Stripe paid → docs OneDrive + email), Dunning Stripe failed (relance 3 jours), Hot Lead Follow-up, Lead Converted Welcome, Form Submitted Notification, etc. Cliquez 'Instancier' pour créer votre version éditable.",
      },
      {
        title: "Dashboard Automations (Marketing Hub)",
        body: "Le Hub Marketing affiche un tableau de bord temps réel des automations : workflows actifs aujourd'hui, leads en délai d'attente, emails IA envoyés, ROI net (revenue estimé - cost emails/IA). Auto-refresh toutes les 30 secondes.",
      },
    ],
  },
  messagerie_meta: {
    title: "Messagerie Unifiée Meta",
    category: "marketing",
    audience: ["admin", "tenant_admin"],
    icon: "MessageCircle",
    sections: [
      {
        title: "WhatsApp + Messenger + Instagram dans une seule Inbox",
        body: "Centralisez tous vos DMs réseaux sociaux dans /admin/messagerie. Layout 3 colonnes : threads à gauche (filtres canal, assignation, non-lus), conversation au centre, panneau IA à droite (classification + suggestion de réponse).",
      },
      {
        title: "Configuration Meta",
        body: "Settings > API > Messagerie Meta : saisissez App ID, App Secret, Access Token (System User permanent recommandé), Verify Token, WhatsApp Phone Number ID, Pages FB et comptes IG. Copiez l'URL Webhook fournie et collez-la dans Meta App Dashboard > Webhooks (events: messages, message_echoes, messaging_postbacks).",
      },
      {
        title: "Agent IA mixte (3 modes)",
        body: "Auto = l'IA répond seule pour les FAQ simples avec confiance ≥85%. Suggestion = l'IA classifie (6 catégories : FAQ, Question technique, Réclamation, Prospect qualifié, Spam, Autre) et propose une réponse, vous validez d'1 clic. Manuel = IA classifie uniquement, zéro envoi automatique.",
      },
      {
        title: "Templates de réponses rapides /cmd",
        body: "Settings > Templates : créez des raccourcis '/bonjour', '/prix', '/rappel'. Dans l'inbox, tapez '/' au début d'un message → menu déroulant avec filtre live + insertion automatique avec interpolation des variables ({{prospect.name}}, {{thread.channel}}, etc.).",
      },
      {
        title: "Assignation et statuts",
        body: "Chaque thread peut être assigné à un membre de l'équipe (dropdown dans le header conversation). Statuts : Open / Closed (résolu) / Spam. Bouton 'M'assigner les non assignées' sur le widget Dashboard pour 1-clic.",
      },
      {
        title: "Stats et analytics",
        body: "/admin/messagerie/stats : 5 KPIs (Volume, Réponses <5min, Auto IA, NPS proxy, Threads actifs), heatmap 7×24 horaire pour repérer vos heures de pic, top 10 contacts les plus actifs, top intents IA.",
      },
      {
        title: "Liaison automatique CRM (Iter 224)",
        body: "Premier message WhatsApp d'un numéro inconnu → prospect auto-créé (source='meta_whatsapp'). Si le numéro existe déjà comme prospect, le thread est lié automatiquement (matching multi-variantes +33/33/0). Pour FB/IG, linkage manuel via 'Lier à prospect existant'.",
      },
    ],
  },
  google_contacts_sync: {
    title: "Synchronisation Google Contacts",
    category: "modules",
    audience: ["admin", "tenant_admin"],
    icon: "Users",
    sections: [
      {
        title: "Importez vos contacts Google en 1 clic",
        body: "Settings > API > Google Contacts. Cliquez 'Connecter Google Contacts' pour autoriser le scope contacts.readonly. Puis 'Lancer la synchro' pour importer jusqu'à 5000 contacts comme prospects.",
      },
      {
        title: "Match intelligent",
        body: "Match par email exact + variantes phone (+33/33/0). Si match : soft update (complète seulement les champs vides, préserve vos données Blitzz). Si non-match : crée prospect avec source='google_contacts' et tag 'google-contacts'.",
      },
    ],
  },
  facturation: {
    title: "Facturation B2B",
    category: "facturation",
    audience: ["admin", "tenant_admin", "comptable", "commercial"],
    icon: "FileText",
    sections: [
      {
        title: "Devis, factures, avoirs",
        body: "Créez des documents unifiés (devis/facture/avoir) avec lignes, TVA, totaux automatiques. Envoyez par email (Gmail OAuth requis). Signature électronique des devis avec automation chain (création inscription LMS automatique).",
      },
      {
        title: "Templates",
        body: "Personnalisez le rendu PDF avec des templates : couleurs, header/footer, CGV, mentions légales, logo. Un template par défaut par type de document.",
      },
      {
        title: "Relances automatiques",
        body: "Configuration > Relances : intervalles (par défaut 7/15/30 jours), template email. Les factures impayées sont automatiquement relancées selon vos règles.",
      },
      {
        title: "Storecove (Peppol/Chorus Pro)",
        body: "Si vous facturez le secteur public ou international, Storecove permet l'envoi via le réseau Peppol. Configurez votre Legal Entity ID dans les paramètres.",
      },
    ],
  },
  ppf_factur_x: {
    title: "PPF Factur-X (B2B France 2026)",
    category: "facturation",
    audience: ["admin", "tenant_admin", "comptable"],
    icon: "FileSignature",
    sections: [
      {
        title: "Qu'est-ce que la PPF ?",
        body: "À partir de septembre 2026, toutes les factures B2B en France doivent être dématérialisées via la Plateforme Publique de Facturation (PPF) ou un Portail de Dématérialisation Privé (PDP). Le format technique est Factur-X (PDF/A-3 + XML CII embarqué).",
      },
      {
        title: "Choisir un opérateur PDP",
        body: "Allez dans Facturation B2B > Configuration PPF. Choisissez parmi 5 opérateurs partenaires : Yooz, Generix, Esker, Tenor, Docaposte. Chaque opérateur a son tarif et sa promesse de service. Saisissez vos credentials API (chiffrés au repos avec Fernet).",
      },
      {
        title: "Conservation légale 10 ans",
        body: "Toutes les factures Factur-X générées sont automatiquement persistées en base avec hash SHA256, taille et timestamp. Téléchargeable via l'endpoint /factur-x.pdf à tout moment pour la durée légale française de 10 ans.",
      },
    ],
  },
  formation_lms: {
    title: "Formation & LMS",
    category: "formation",
    audience: ["formateur", "admin", "tenant_admin"],
    icon: "GraduationCap",
    sections: [
      {
        title: "Programmes & modules",
        body: "Créez vos programmes de formation puis structurez-les en modules. Chaque module contient des séquences (vidéos, quiz, exercices, ressources PDF).",
      },
      {
        title: "Sessions",
        body: "Une session = un déroulé concret d'un programme avec dates, formateur(s), lieu, capacité. Les inscriptions remontent automatiquement depuis vos devis signés.",
      },
      {
        title: "Évaluations",
        body: "Module Évaluations : créez des templates (satisfaction, NPS, à chaud/à froid). Envoyez aux stagiaires par email. Réponses visibles en agrégat ou détaillé.",
      },
      {
        title: "Conformité Qualiopi",
        body: "Le module Conformité Qualiopi génère automatiquement les documents requis : convocations, conventions, attestations, livrets stagiaires. Tout est tagué avec les indicateurs Qualiopi correspondants.",
      },
      {
        title: "Stats BPF",
        body: "Le module Stats BPF agrège vos données année civile pour le Bilan Pédagogique et Financier (CERFA 10443). Export Excel + PDF prêt à transmettre à votre OPCO.",
      },
    ],
  },
  certifications_rncp: {
    title: "Certifications RS/RNCP",
    category: "formation",
    audience: ["admin", "tenant_admin", "formateur"],
    icon: "Award",
    sections: [
      {
        title: "Créer une certification",
        body: "Formation > Certifications RS/RNCP > Nouvelle. Saisissez : type (RS/RNCP), code, titre, niveau, blocs de compétences. Vous pouvez importer le XML France Compétences pour pré-remplir les blocs.",
      },
      {
        title: "Système de portage",
        body: "Le portage permet de partager une certification avec d'autres centres de formation (tenants). Vous (certificateur) recevez une commission sur les inscriptions remontées par les centres porteurs.",
      },
      {
        title: "Jurys & candidats",
        body: "Convoquez des jurys (présentiel/distanciel/hybride) avec membres (président, examinateurs, professionnels). Inscrivez vos candidats et suivez leur parcours bloc par bloc.",
      },
      {
        title: "Parchemin officiel + QR",
        body: "Une fois tous les blocs validés, générez le parchemin PDF officiel avec QR code unique. Le QR pointe vers une URL publique de vérification (sans authentification) — équivalent du registre RNCP.",
      },
    ],
  },
  master_overview: {
    title: "Master Admin — vue d'ensemble",
    category: "master",
    audience: ["superadmin"],
    icon: "Crown",
    sections: [
      {
        title: "Tenants",
        body: "Liste de tous les tenants (clients) de la plateforme. Vue détaillée par tenant : utilisateurs, modules actifs, abonnement, transactions Stripe.",
      },
      {
        title: "Constructeur d'offres",
        body: "Créez ou modifiez les offres SaaS proposées aux tenants. Pour chaque offre : nom, description, prix mensuel/annuel, modules inclus, multi-sociétés, marque blanche, paiement Stripe self-service.",
      },
      {
        title: "Bibliothèque d'addons",
        body: "Modules vendus séparément aux tenants en plus de leur offre principale. Configurable avec prix unitaire.",
      },
      {
        title: "Abonnements",
        body: "Vue de tous les abonnements actifs (offre + tenant + statut). Bouton 'Facturer' pour générer un lien Stripe Checkout ad-hoc (renouvellement, frais, support).",
      },
    ],
  },
  offers_builder: {
    title: "Constructeur d'offres",
    category: "master",
    audience: ["superadmin"],
    icon: "Package",
    sections: [
      {
        title: "Créer une offre",
        body: "Master Admin > Offres > Nouvelle offre. Renseignez nom, description, prix mensuel/annuel, max users/prospects, et sélectionnez les modules à inclure (cochage par catégorie).",
      },
      {
        title: "Modules disponibles",
        body: "Les modules sont organisés par catégorie : Mon Espace, Force de vente, Formation, Marketing, Outils, Gestion, Facturation B2B, Multi-Sociétés, Pilotage, Automatisation, Intégrations.",
      },
      {
        title: "Paiement Stripe self-service",
        body: "Toggle 'Paiement Stripe self-service' (recommandé). Si activé, les tenants peuvent souscrire eux-mêmes via Stripe Checkout. Sinon, vous attribuez l'abonnement manuellement depuis l'onglet Abonnements.",
      },
      {
        title: "Multi-sociétés et marque blanche",
        body: "Toggles dédiés pour permettre au tenant de créer plusieurs sociétés et/ou personnaliser sa marque (logo, couleurs). Chacune avec frais mensuels/annuels configurables.",
      },
    ],
  },
  stripe_config: {
    title: "Configuration Stripe (master)",
    category: "master",
    audience: ["superadmin"],
    icon: "CreditCard",
    sections: [
      {
        title: "Onglet Configuration Stripe",
        body: "Master Admin > Configuration Stripe affiche : statut (Test Emergent / Test / Live / Manquant), clé API masquée, URL webhook à configurer dans Stripe Dashboard, stats agrégées (transactions, CA, CPA).",
      },
      {
        title: "Mode test vs production",
        body: "Par défaut, la plateforme utilise la clé Test Emergent (`sk_test_emergent`). Aucun débit réel. Carte de test : 4242 4242 4242 4242 - 12/34 - 123. Pour passer en live, créez un compte Stripe puis remplacez la clé dans backend/.env.",
      },
      {
        title: "Webhook Stripe",
        body: "Copiez l'URL webhook fournie. Dans Stripe Dashboard > Developers > Webhooks > Add endpoint, créez un endpoint avec cette URL et l'événement 'checkout.session.completed'. Le webhook met automatiquement à jour les statuts des transactions.",
      },
      {
        title: "Facturer un tenant ad-hoc",
        body: "Onglet Abonnements > bouton 'Facturer' à côté du tenant. Saisissez montant + description. Un lien Stripe Checkout est généré : copiez-le et envoyez-le à votre client par email. Idéal pour renouvellements ponctuels.",
      },
    ],
  },
  release_management: {
    title: "Gestion des releases",
    category: "master",
    audience: ["superadmin"],
    icon: "GitBranch",
    sections: [
      {
        title: "Notes de release",
        body: "Chaque déploiement de la plateforme est documenté dans /aide > Notes de release. Vous y trouvez : version, date, titre, items modifiés, et actions admin recommandées (s'il y en a).",
      },
      {
        title: "Routine post-déploiement",
        body: "À chaque grosse mise à jour, vérifiez : (1) si de nouveaux modules ont été ajoutés au constructeur d'offres → faut-il les inclure dans Pack Business ? (2) si vos tenants existants doivent recevoir les nouveaux modules → utilisez les scripts de migration. (3) si les rôles équipe doivent être ajustés.",
      },
      {
        title: "Scripts de migration",
        body: "Les scripts /app/backend/scripts/migration_iter*.py sont idempotents (lançables plusieurs fois sans risque). Ils sont à exécuter en prod après déploiement quand la note de release l'indique. Exemple : `python scripts/migration_iter196_formation_certifications.py`",
      },
    ],
  },
  formateur_space: {
    title: "Espace Formateur",
    category: "formation",
    audience: ["formateur"],
    icon: "GraduationCap",
    sections: [
      {
        title: "Mes sessions",
        body: "Liste des sessions auxquelles vous êtes assigné. Pour chacune : agenda, stagiaires inscrits, ressources pédagogiques.",
      },
      {
        title: "Mon agenda",
        body: "Vue calendrier de toutes vos prestations (cours, jurys, accompagnements). Synchronisable avec Google Calendar (via l'intégration).",
      },
      {
        title: "Évaluations à corriger",
        body: "Notification push automatique quand un stagiaire soumet une évaluation. Accédez à la liste depuis Mon Espace > Évaluations à corriger.",
      },
    ],
  },
  stagiaire_space: {
    title: "Espace Stagiaire",
    category: "formation",
    audience: ["stagiaire"],
    icon: "BookOpen",
    sections: [
      {
        title: "Mes parcours",
        body: "Vue de vos formations en cours et terminées. Cliquez sur un parcours pour accéder aux séquences (vidéos, quiz, exercices).",
      },
      {
        title: "Mes évaluations",
        body: "Liste des évaluations à compléter (à chaud, à froid, satisfaction). Chacune a une deadline indiquée.",
      },
      {
        title: "Replay des évaluations passées",
        body: "Vous pouvez consulter vos réponses et notes obtenues sur les évaluations passées dans Mes parcours > Replay.",
      },
    ],
  },

  // ─── Lot 1+2 (Iter 233-236) — Nouveaux topics ───
  affiliation: {
    title: "Affiliation longue durée (commission à la signature)",
    category: "marketing",
    audience: ["all"],
    icon: "Handshake",
    sections: [
      {
        title: "Le concept",
        body: "Tout lead apporté par un partenaire (lien d'affiliation ou iframe) est attribué durablement à ce partenaire. La commission n'est versée qu'au moment de la **vente réelle** (Stripe payé / opportunité gagnée / formation déclenchée), pas à la simple inscription. Idéal pour rémunérer un apporteur d'affaires sans risque sur les leads non convertis.",
      },
      {
        title: "3 types de partenaires",
        body: "1. **Tenant partenaire** — un autre OF du SaaS Blitzz.\n2. **Apporteur externe** — nom + email + IBAN, sans compte.\n3. **Commercial interne** — un membre de votre équipe ambassadeur.",
      },
      {
        title: "Créer un lien d'affiliation",
        body: "Allez dans `/admin/marketing/affiliation` → onglet **Liens** → bouton **Nouveau lien**. Choisissez le partenaire, le pourcentage de commission (ex: 25%), et copiez l'URL générée. Cette URL fonctionne en redirect public ou en iframe avec `?aff=...`.",
      },
      {
        title: "Verrouillage + réattribution",
        body: "L'attribution est verrouillée à vie par défaut. Un admin peut réattribuer un prospect manuellement avec un motif obligatoire (audit log conservé).",
      },
      {
        title: "Versement des commissions",
        body: "Tableau de bord avec KPIs : leads attribués, en attente de signature, à verser, versées. Bouton **Marquer comme payée** avec ref paiement. Une facture interne `commission_affiliate` est générée automatiquement.",
      },
    ],
  },

  yousign_signature: {
    title: "Signature électronique Yousign (eIDAS Avancé)",
    category: "facturation",
    audience: ["all"],
    icon: "ShieldCheck",
    sections: [
      {
        title: "Niveaux de signature supportés",
        body: "Trois niveaux conformes au règlement eIDAS européen :\n- **Simple** : signature electronique de base, suffisant pour devis/contrats internes.\n- **Avancé** (recommandé) : OTP par email, valeur juridique forte, conforme Qualiopi 2025+.\n- **Qualifié** : eIDAS QES, équivaut à une signature manuscrite en France.",
      },
      {
        title: "Activer Yousign",
        body: "1. Créer un compte sur [yousign.com/fr](https://yousign.com/fr) (sandbox gratuit illimité).\n2. Récupérer la clé API et le webhook secret HMAC.\n3. Aller dans **Paramètres → API → Yousign**, coller les clés, activer le toggle sandbox/production.\n4. Copier l'URL Webhook fournie et l'enregistrer dans le dashboard Yousign.",
      },
      {
        title: "Envoyer un devis à signer",
        body: "Sur la liste des devis (status brouillon ou envoyé), cliquez sur **Signature électronique** → choisissez le niveau, ajoutez ou détectez le client signataire, validez. Le client reçoit un email Yousign + un email Blitzz brandé avec QR code (scannable depuis smartphone).",
      },
      {
        title: "Conventions Qualiopi (multi-signataires)",
        body: "Pour les conventions de formation, le système détecte automatiquement l'apprenant + l'organisme + l'employeur (signataires multiples). Chacun reçoit son lien et son QR code. À la signature complète, la commission affiliation se déclenche automatiquement (si lead apporté par un partenaire).",
      },
      {
        title: "QR code mobile pour stagiaires",
        body: "Chaque signataire reçoit un email avec QR code embarqué (PNG inline) pointant vers Yousign. Le stagiaire scanne avec son smartphone, reçoit l'OTP sur la même boîte mail, signe en 30 secondes. Idéal pour les chantiers BTP/terrain.",
      },
    ],
  },

  intel_agent: {
    title: "Agent IA enquêteur (Tavily + Pappers + Claude)",
    category: "ai",
    audience: ["all"],
    icon: "Search",
    sections: [
      {
        title: "Le concept",
        body: "Depuis chaque fiche prospect, un bouton **🔍 Enquête IA** lance un agent qui :\n1. **Fouille le web** via Tavily (3 recherches en parallèle : général, LinkedIn, presse).\n2. **Interroge Pappers** pour les données légales FR (SIREN, dirigeants, BODACC, procédures collectives).\n3. **Synthétise** via Claude Sonnet 4.5 en gérant les homonymes (vérification cohérence email domain ↔ site web société).\n\nRésultat : une fiche complète avec niveau de risque, signaux faibles, profils sociaux.",
      },
      {
        title: "Activer l'agent",
        body: "1. Créer un compte sur [tavily.com](https://tavily.com) (~$5/1000 recherches).\n2. Créer un compte sur [pappers.fr](https://pappers.fr/api) (~50€/mois pour 5000 requêtes).\n3. Aller dans **Paramètres → API → Comptabilité → Agent IA enquêteur**, coller les 2 clés.\n4. Lancer une enquête depuis n'importe quelle fiche prospect.",
      },
      {
        title: "Cache et quota",
        body: "Chaque enquête est mise en cache 30 jours (déterministe sur nom + email + société). 2 enquêtes consécutives sur le même prospect dans les 30 jours = cache instantané (gratuit). Quota mensuel par tenant : 50 par défaut (configurable). Coût estimé : ~0.12€/enquête.",
      },
      {
        title: "Lecture du rapport",
        body: "Modal 5 onglets :\n- **Synthèse** : verdict + niveau de risque (low/medium/high) + score de désambiguïsation homonyme.\n- **Société** : SIREN, KBIS, dirigeants, capital, effectif.\n- **Presse** : 5 derniers articles datés.\n- **Social** : LinkedIn, Twitter, Facebook détectés.\n- **Risques** : procédures collectives, BODACC, alertes.",
      },
    ],
  },

  welcome_stagiaire: {
    title: "Welcome Stagiaire J-7 + Quiz Qualiopi",
    category: "formation",
    audience: ["all", "stagiaire"],
    icon: "Mail",
    sections: [
      {
        title: "Email automatique J-7",
        body: "7 jours avant le démarrage d'une session, chaque inscrit reçoit un email Resend brandé contenant : planning détaillé, profils des formateurs (photos + bio), photos des locaux, convention pré-signature Yousign avec QR code, et lien vers le quiz de positionnement.",
      },
      {
        title: "Conformité Qualiopi 2025+ indicateur 12",
        body: "Le quiz couvre l'indicateur 12 (**Adaptation aux bénéficiaires**). Score stocké dans `prospect.pre_formation_score` + activité timeline. La preuve audit Qualiopi est générée automatiquement.",
      },
      {
        title: "Types de questions supportés",
        body: "- **QCM** (radio) : une seule bonne réponse.\n- **QCM multi** (checkbox) : plusieurs bonnes réponses, scoring Jaccard.\n- **Échelle 1-5** : auto-évaluation linéaire.\n- **Texte court / long** : score si > 5 caractères.",
      },
      {
        title: "Reco IA pour le formateur",
        body: "À la soumission, Claude Sonnet 4.5 génère pour le formateur :\n- Résumé du profil du stagiaire (profil débutant/avancé)\n- Forces et faiblesses\n- Conseils pédagogiques\n- Rythme recommandé\n- Module de départ suggéré",
      },
      {
        title: "Alertes score faible",
        body: "Si le score global < 40 (niveau Débutant), une alerte est créée automatiquement dans `formateur_alerts` + push notification sur le navigateur du formateur. Visible aussi dans la page **Mes Sessions** côté formateur (carte amber dépliable).",
      },
      {
        title: "Templates personnalisables",
        body: "Hiérarchie : session override > global tenant > default codé en dur. Variables interpolées : `{{learner_first_name}}`, `{{session_name}}`, `{{organism_name}}`, `{{start_date_human}}`, etc. Editable dans `/admin/welcome/templates`.",
      },
    ],
  },

  time_tracking: {
    title: "Pointage GPS + SMS Twilio + QR codes",
    category: "modules",
    audience: ["all"],
    icon: "Clock",
    sections: [
      {
        title: "Le concept",
        body: "Module complet de pointage des salariés sur le terrain via :\n- **App mobile** (PunchPage `/punch`) avec auto-géolocalisation\n- **QR code rotatif** (change toutes les 20 min) sur les postes fixes\n- **OTP SMS Twilio** (code 6 chiffres) pour authentifier l'employé\n- **Géofence** Haversine (rayon configurable par poste)\n- **Mode offline** avec sync automatique au retour online",
      },
      {
        title: "Activer Twilio SMS",
        body: "1. Créer un compte sur [twilio.com](https://twilio.com) (15$ de crédit gratuit).\n2. Récupérer Account SID + Auth Token + From Number (E.164 ex: +33...).\n3. Aller dans **Paramètres → API → Pointage salariés**, coller les 3 valeurs.\n4. Coût : ~6c€ par SMS envoyé.",
      },
      {
        title: "Configurer un terminal (poste fixe)",
        body: "Dans la carte Pointage, créer un terminal (label, lat/lng, rayon en mètres). Le système génère un QR code rotatif (token déterministe SHA256 sur fenêtre de 20 min — pas de DB nécessaire). Imprimez-le et affichez-le sur le poste.",
      },
      {
        title: "Géofence et validation manager",
        body: "Si l'employé pointe en dehors du rayon configuré (Haversine), le pointage est marqué `outside` et nécessite une validation manager. Visible dans `/admin/punch` → onglet **À valider**. Boutons Valider/Refuser inline.",
      },
      {
        title: "Timesheet mensuel pour fiches de paie",
        body: "Endpoint `GET /tracking/employees/{user_id}/timesheet?month=YYYY-MM` retourne : total minutes, total heures, jours travaillés, moyenne min/jour. Exportable pour traitement paie.",
      },
      {
        title: "Mode offline",
        body: "Si l'employé n'a pas de connexion (chantier), les pointages sont stockés dans `localStorage.punch_offline_queue` et sync automatique au retour online via `POST /tracking/sync-offline`. Les pointages > 48h sont rejetés.",
      },
    ],
  },

  integrations_health: {
    title: "Contrôle des intégrations API",
    category: "security",
    audience: ["admin", "superadmin", "tenant_admin"],
    icon: "Activity",
    sections: [
      {
        title: "Vue d'ensemble",
        body: "La page **Contrôle API** (`/admin/integrations-health`) scanne en live le statut de chacune des 13+ intégrations tierces de la plateforme : MongoDB, Emergent LLM, Stripe, Resend, Aircall, Twilio, Yousign, Meta, Tavily/Pappers, OneDrive, Google Contacts, Pennylane, Apple Wallet.",
      },
      {
        title: "4 statuts possibles",
        body: "- ✅ **OK** : intégration configurée + ping live OK.\n- ⚠️ **Avertissement** : configurée mais ping partiel ou réponse incomplète.\n- ❌ **Erreur** : configurée mais erreur réseau ou clé invalide.\n- 🔌 **Non configuré** : intégration non activée (clé absente).",
      },
      {
        title: "Auto-refresh 60s",
        body: "La page se rafraîchit automatiquement toutes les minutes. Bouton **Rafraîchir** disponible pour un check manuel immédiat.",
      },
      {
        title: "Scope tenant vs master",
        body: "- **tenant_admin** : voit uniquement les intégrations de son tenant.\n- **admin/superadmin sans tenant_id** : voit l'agrégat cross-tenant (ex: combien de tenants ont configuré Resend).",
      },
    ],
  },
};

/**
 * Filtre les topics visibles selon le rôle utilisateur + business roles.
 */
export function getVisibleTopics(userRole, businessRoles = []) {
  const out = {};
  Object.entries(HELP_TOPICS).forEach(([id, topic]) => {
    const audience = topic.audience || [];
    if (audience.includes("all")) { out[id] = topic; return; }
    if (audience.includes(userRole)) { out[id] = topic; return; }
    if (businessRoles.some(r => audience.includes(r))) { out[id] = topic; return; }
  });
  return out;
}
